// src/pages/task/views/ScheduleView.tsx
// Weekly workforce planning board: one row per assignee, one column per day.
//
// Rows are fed by three sources that the page merges for us:
//   * the loaded task page (so people with no dated work are still visible),
//   * ListScheduledTasks for the visible week (real work blocks), and
//   * GetAssigneeAvailability (busy / free capacity behind each day).
//
// Blocks can be dragged to another day (reschedule) or another person
// (reassign, behind a confirmation).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Alert, Button, ErrorAlert } from '@react/components';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

import type { Actor } from '@core/types/actor';
import type { ActorSchedule, Task, TaskStatusDefinition } from '@core/types/task';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  getAvatarColor,
  getDisplayName,
  getEntityId,
  getInitials,
  getTaskAssignee,
} from '@/features/task/logic';

import {
  UNASSIGNED_KEY,
  addDays,
  dateKey,
  formatDayCellHeader,
  formatHours,
  formatTimeRange,
  getActorKey,
  getPlannedDate,
  getScheduledRange,
  getWeekDays,
  minutesInDay,
  minutesInRange,
  resolveStatusStyle,
  startOfDay,
  type StatusStyle,
} from './planning';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ScheduleMoveTarget {
  actorKey: string;
  actor: Actor | null;
  date: Date;
}

interface ScheduleViewProps {
  /** The loaded workspace page — used so every assignee gets a row. */
  tasks: Task[];
  /** Tasks with work blocks in the visible week (ListScheduledTasks). */
  scheduledTasks?: Task[];
  /** GetAssigneeAvailability result, keyed by actor id. */
  availability?: Record<string, ActorSchedule>;
  statusDefinitions?: TaskStatusDefinition[];
  isScheduleLoading?: boolean;
  isAvailabilityLoading?: boolean;
  scheduleError?: unknown;
  availabilityError?: unknown;
  onRetrySchedule?: () => void;
  /** Fires whenever the visible week changes, so the page can refetch. */
  onRangeChange?: (range: { start: Date; end: Date }) => void;
  onTaskClick: (id: string) => void;
  /** Move a block to another day and/or another person. */
  onMove?: (taskId: string, target: ScheduleMoveTarget) => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Row model
// ---------------------------------------------------------------------------

interface ScheduleBlock {
  id: string;
  title: string;
  style: StatusStyle;
  timeLabel: string;
  minutes: number;
  isScheduled: boolean;
}

interface PersonRow {
  key: string;
  actor: Actor | null;
  name: string;
  blocksByDay: Map<string, ScheduleBlock[]>;
  scheduledMinutes: number;
}

const WEEK_STARTS_ON = 1; // Monday

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScheduleView = observer(({
  tasks,
  scheduledTasks,
  availability,
  statusDefinitions,
  isScheduleLoading = false,
  isAvailabilityLoading = false,
  scheduleError,
  availabilityError,
  onRetrySchedule,
  onRangeChange,
  onTaskClick,
  onMove,
}: ScheduleViewProps) => {
  const { t, locale } = useI18n();
  const today = useMemo(() => startOfDay(new Date()), []);
  const todayKey = dateKey(today);

  const [weekOffset, setWeekOffset] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<
    { taskId: string; taskTitle: string; target: ScheduleMoveTarget; fromName: string } | null
  >(null);
  const [isMoving, setIsMoving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const weekDays = useMemo(
    () => getWeekDays(addDays(today, weekOffset * 7), WEEK_STARTS_ON),
    [today, weekOffset],
  );
  const weekStart = weekDays[0];
  const weekEnd = useMemo(() => addDays(weekDays[6], 1), [weekDays]);

  // Tell the page which window to load. Keyed on the ISO bounds so an
  // unchanged week never re-triggers a fetch.
  const notifyRange = useRef(onRangeChange);
  useEffect(() => {
    notifyRange.current = onRangeChange;
  }, [onRangeChange]);
  useEffect(() => {
    notifyRange.current?.({ start: weekStart, end: weekEnd });
  }, [weekStart, weekEnd]);

  const dayKeys = useMemo(() => weekDays.map(dateKey), [weekDays]);

  // ── Rows ────────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    // A task present in both sources is taken from the scheduled result,
    // which is the one that carries the work block.
    const merged = new Map<string, Task>();
    for (const task of tasks) merged.set(getEntityId(task), task);
    for (const task of scheduledTasks ?? []) merged.set(getEntityId(task), task);

    const byKey = new Map<string, PersonRow>();
    const weekKeys = new Set(dayKeys);

    for (const task of merged.values()) {
      const assignee = getTaskAssignee(task);
      const key = getActorKey(assignee);
      let row = byKey.get(key);
      if (!row) {
        row = {
          key,
          actor: key === UNASSIGNED_KEY ? null : (assignee as Actor),
          name: key === UNASSIGNED_KEY ? t('task.unassigned') : getDisplayName(assignee),
          blocksByDay: new Map(),
          scheduledMinutes: 0,
        };
        byKey.set(key, row);
      }

      const planned = getPlannedDate(task);
      if (!planned) continue;
      const plannedKey = dateKey(planned);
      if (!weekKeys.has(plannedKey)) continue;

      const range = getScheduledRange(task);
      const block: ScheduleBlock = {
        id: getEntityId(task),
        title: (task as unknown as { title?: string }).title || t('common.untitled'),
        style: resolveStatusStyle(task, statusDefinitions),
        timeLabel: range && !range.isAllDay ? formatTimeRange(range.start, range.end, locale) : '',
        minutes: range?.minutes ?? 0,
        isScheduled: !!range,
      };
      row.scheduledMinutes += block.minutes;
      const bucket = row.blocksByDay.get(plannedKey);
      if (bucket) bucket.push(block);
      else row.blocksByDay.set(plannedKey, [block]);
    }

    return [...byKey.values()].sort((a, b) => {
      if (a.key === UNASSIGNED_KEY) return 1;
      if (b.key === UNASSIGNED_KEY) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [tasks, scheduledTasks, dayKeys, statusDefinitions, locale, t]);

  const blockIndex = useMemo(() => {
    const map = new Map<string, { block: ScheduleBlock; row: PersonRow; dayKey: string }>();
    for (const row of rows) {
      for (const [day, blocks] of row.blocksByDay) {
        for (const block of blocks) map.set(block.id, { block, row, dayKey: day });
      }
    }
    return map;
  }, [rows]);

  const totalBlocks = blockIndex.size;

  // ── Drag & drop ─────────────────────────────────────────────────────
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || !onMove) return;

      const taskId = String(active.id);
      const [targetRowKey, targetDayKey] = String(over.id).split('|');
      const source = blockIndex.get(taskId);
      if (!source || !targetRowKey || !targetDayKey) return;
      if (source.row.key === targetRowKey && source.dayKey === targetDayKey) return;

      const targetRow = rows.find((row) => row.key === targetRowKey);
      const targetDate = weekDays.find((day) => dateKey(day) === targetDayKey);
      if (!targetRow || !targetDate) return;

      const target: ScheduleMoveTarget = {
        actorKey: targetRow.key,
        actor: targetRow.actor,
        date: targetDate,
      };

      // Reassignment is a bigger deal than a reschedule: confirm it.
      if (targetRow.key !== source.row.key) {
        setPendingMove({
          taskId,
          taskTitle: source.block.title,
          target,
          fromName: source.row.name,
        });
        return;
      }

      void onMove(taskId, target);
    },
    [blockIndex, onMove, rows, weekDays],
  );

  const confirmMove = useCallback(async () => {
    if (!pendingMove || !onMove) return;
    setIsMoving(true);
    try {
      await onMove(pendingMove.taskId, pendingMove.target);
      setPendingMove(null);
    } finally {
      setIsMoving(false);
    }
  }, [onMove, pendingMove]);

  const activeBlock = activeId ? blockIndex.get(activeId)?.block ?? null : null;

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setWeekOffset((o) => o - 1)} aria-label={t('common.previous')}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <span className="min-w-[210px] text-center text-sm font-semibold text-foreground">
            {formatDayCellHeader(weekDays[0], locale)} &mdash; {formatDayCellHeader(weekDays[6], locale)}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setWeekOffset((o) => o + 1)} aria-label={t('common.next')}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setWeekOffset(0)}>
            {t('workspace.calendar.today')}
          </Button>
          {isScheduleLoading && (
            <span className="text-xs text-muted-foreground">{t('common.loading')}</span>
          )}
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{rows.length} {t('task.assignees')}</span>
          <span>{totalBlocks} {t('task.tasks')}</span>
          {onMove && <span className="hidden sm:inline">{t('task.planning.scheduleHint')}</span>}
        </div>
      </div>

      {/* Async surfaces */}
      {scheduleError ? (
        <div className="space-y-2">
          <ErrorAlert error={scheduleError} />
          {onRetrySchedule && (
            <Button size="sm" variant="secondary" onClick={onRetrySchedule}>
              {t('common.retry')}
            </Button>
          )}
        </div>
      ) : null}

      {availabilityError ? (
        <Alert variant="warning">{t('task.planning.availabilityUnavailable')}</Alert>
      ) : null}

      {/* Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Day headers */}
              <div className="grid grid-cols-8 border-b border-border bg-muted/30">
                <div className="border-r border-border p-3 text-xs font-semibold text-muted-foreground">
                  {t('task.assignee')}
                </div>
                {weekDays.map((day) => {
                  const key = dateKey(day);
                  return (
                    <div
                      key={key}
                      className={`border-r border-border p-3 text-center text-xs font-semibold last:border-r-0 ${
                        key === todayKey ? 'bg-primary/5 text-primary' : 'text-muted-foreground'
                      }`}
                    >
                      {formatDayCellHeader(day, locale)}
                    </div>
                  );
                })}
              </div>

              {/* Rows */}
              {isScheduleLoading && rows.length === 0 ? (
                <ScheduleSkeleton />
              ) : rows.length === 0 ? (
                <div className="flex h-48 flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
                  <p>{t('task.noScheduledTasks')}</p>
                  <p className="text-xs">{t('task.planning.scheduleEmptyHint')}</p>
                </div>
              ) : (
                rows.map((row) => (
                  <ScheduleRow
                    key={row.key}
                    row={row}
                    weekDays={weekDays}
                    todayKey={todayKey}
                    schedule={availability?.[row.key]}
                    isAvailabilityLoading={isAvailabilityLoading}
                    weekStart={weekStart}
                    weekEnd={weekEnd}
                    draggable={!!onMove}
                    activeId={activeId}
                    onTaskClick={onTaskClick}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeBlock && (
            <div className="rounded border border-border bg-card px-2 py-1 text-xs shadow-lg">
              <span className="flex items-center gap-1.5">
                <span
                  className={`h-2 w-2 rounded-full ${activeBlock.style.className}`}
                  style={activeBlock.style.hex ? { backgroundColor: activeBlock.style.hex } : undefined}
                />
                <span className="truncate">{activeBlock.title}</span>
              </span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <ConfirmDialog
        open={!!pendingMove}
        title={t('task.planning.reassignTitle')}
        description={
          pendingMove
            ? t('task.planning.reassignBody', {
                title: pendingMove.taskTitle,
                from: pendingMove.fromName,
                to: pendingMove.target.actor
                  ? getDisplayName(pendingMove.target.actor)
                  : t('task.unassigned'),
              })
            : undefined
        }
        confirmLabel={t('task.planning.reassignConfirm')}
        isConfirming={isMoving}
        onConfirm={() => void confirmMove()}
        onCancel={() => setPendingMove(null)}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface ScheduleRowProps {
  row: PersonRow;
  weekDays: Date[];
  todayKey: string;
  schedule?: ActorSchedule;
  isAvailabilityLoading: boolean;
  weekStart: Date;
  weekEnd: Date;
  draggable: boolean;
  activeId: string | null;
  onTaskClick: (id: string) => void;
}

function ScheduleRow({
  row,
  weekDays,
  todayKey,
  schedule,
  isAvailabilityLoading,
  weekStart,
  weekEnd,
  draggable,
  activeId,
  onTaskClick,
}: ScheduleRowProps) {
  const { t } = useI18n();

  const availableMinutes = useMemo(
    () => minutesInRange(schedule?.free_slots, weekStart, weekEnd),
    [schedule, weekStart, weekEnd],
  );
  const load = availableMinutes > 0
    ? Math.min(100, Math.round((row.scheduledMinutes / availableMinutes) * 100))
    : 0;

  return (
    <div className="grid grid-cols-8 border-b border-border last:border-b-0">
      {/* Person */}
      <div className="flex items-start gap-2 border-r border-border p-3">
        <div
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
          style={{ backgroundColor: getAvatarColor(row.name) }}
        >
          {getInitials(row.name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-foreground">{row.name}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {availableMinutes > 0
              ? t('task.planning.hoursOf', {
                  scheduled: formatHours(row.scheduledMinutes),
                  available: formatHours(availableMinutes),
                })
              : isAvailabilityLoading
                ? t('common.loading')
                : t('task.planning.hoursScheduled', { scheduled: formatHours(row.scheduledMinutes) })}
          </p>
          {availableMinutes > 0 && (
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full ${load >= 100 ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${load}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Days */}
      {weekDays.map((day) => (
        <ScheduleCell
          key={dateKey(day)}
          rowKey={row.key}
          day={day}
          isToday={dateKey(day) === todayKey}
          blocks={row.blocksByDay.get(dateKey(day)) ?? []}
          schedule={schedule}
          draggable={draggable}
          activeId={activeId}
          onTaskClick={onTaskClick}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell
// ---------------------------------------------------------------------------

interface ScheduleCellProps {
  rowKey: string;
  day: Date;
  isToday: boolean;
  blocks: ScheduleBlock[];
  schedule?: ActorSchedule;
  draggable: boolean;
  activeId: string | null;
  onTaskClick: (id: string) => void;
}

function ScheduleCell({
  rowKey,
  day,
  isToday,
  blocks,
  schedule,
  draggable,
  activeId,
  onTaskClick,
}: ScheduleCellProps) {
  const { t } = useI18n();
  const key = dateKey(day);
  const { setNodeRef, isOver } = useDroppable({ id: `${rowKey}|${key}`, disabled: !draggable });

  const busy = minutesInDay(schedule?.busy_slots, day);
  const free = minutesInDay(schedule?.free_slots, day);
  const capacity = busy + free;
  const busyPercent = capacity > 0 ? Math.round((busy / capacity) * 100) : 0;

  return (
    <div
      ref={setNodeRef}
      className={`relative min-h-[84px] space-y-1 border-r border-border p-1.5 pt-2.5 last:border-r-0 ${
        isToday ? 'bg-primary/5' : ''
      } ${isOver && activeId ? 'ring-2 ring-inset ring-primary/50' : ''}`}
    >
      {/* Capacity strip: busy vs free for this person-day. */}
      {capacity > 0 && (
        <div
          className="absolute inset-x-1.5 top-1 h-1 overflow-hidden rounded-full bg-muted"
          title={t('task.planning.capacity', {
            busy: formatHours(busy),
            free: formatHours(free),
          })}
        >
          <div className="h-full rounded-full bg-primary/40" style={{ width: `${busyPercent}%` }} />
        </div>
      )}

      {blocks.map((block) => (
        <ScheduleBlockChip
          key={block.id}
          block={block}
          draggable={draggable}
          isDragging={activeId === block.id}
          onClick={onTaskClick}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block chip
// ---------------------------------------------------------------------------

interface ScheduleBlockChipProps {
  block: ScheduleBlock;
  draggable: boolean;
  isDragging: boolean;
  onClick: (id: string) => void;
}

function ScheduleBlockChip({ block, draggable, isDragging, onClick }: ScheduleBlockChipProps) {
  const { t } = useI18n();
  const { attributes, listeners, setNodeRef } = useDraggable({ id: block.id, disabled: !draggable });

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      onClick={() => onClick(block.id)}
      title={block.timeLabel ? `${block.timeLabel} · ${block.title}` : block.title}
      className={`block w-full rounded border border-border bg-card px-2 py-1 text-left text-xs transition-colors hover:bg-muted ${
        isDragging ? 'opacity-40' : ''
      } ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <span className="flex items-center gap-1.5">
        <span
          className={`h-2 w-2 flex-shrink-0 rounded-full ${block.style.className}`}
          style={block.style.hex ? { backgroundColor: block.style.hex } : undefined}
        />
        <span className="truncate font-medium text-foreground">{block.title}</span>
      </span>
      <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
        {block.timeLabel || (block.isScheduled ? t('task.planning.allDay') : t('task.dueDate'))}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function ScheduleSkeleton() {
  return (
    <div>
      {Array.from({ length: 4 }).map((_, rowIndex) => (
        <div key={rowIndex} className="grid grid-cols-8 border-b border-border last:border-b-0">
          <div className="flex items-center gap-2 border-r border-border p-3">
            <div className="h-6 w-6 animate-pulse rounded-full bg-muted" />
            <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          </div>
          {Array.from({ length: 7 }).map((__, cellIndex) => (
            <div key={cellIndex} className="min-h-[84px] border-r border-border p-1.5 last:border-r-0">
              {cellIndex % 3 === 0 && <div className="h-8 animate-pulse rounded bg-muted" />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
